import React, { useState } from 'react';
import {
  Box,
  Typography,
  LinearProgress,
  Paper,
  Button,
  TextField,
  Rating,
  Alert,
  Snackbar,
  IconButton,
} from '@mui/material';
import * as MuiIcons from '@mui/icons-material';
import {
  useGetAgentReviewsQuery,
  useDeleteAgentReviewMutation,
  usePostAgentReviewMutation,
  usePutAgentReviewMutation,
} from '../../../data/api/endPointsSlice';
import { useMsal } from '@azure/msal-react';
import ConfirmationDialog from '../../../shared/components/ConfirmationDialog';
import { AgentMarketPlaceItemWithFilter } from './AgentMarketPlace';
import { Review, getUserReview, extractUserData, createReviewPayload, UserAccount } from '../../../utils/reviewHelpers';

interface AgentReviewAnalysisProps {
  agentId: string;
  agentName: string;
  agentOwner: string;
  onReviewChange?: () => void;
  agentDetails: AgentMarketPlaceItemWithFilter;
  onClose?: () => void;
}

interface AgentReviewData {
  rating_distribution: number[];
  reviews: Review[];
  average_rating: number;
  total_count: number;
}

function getRatingDistribution(ratingDistributionData: number[], reviewsArray: Review[]): number[] {
  if (ratingDistributionData && ratingDistributionData.some((value) => value > 0)) {
    return ratingDistributionData;
  }

  const ratingCountsArray = [0, 0, 0, 0, 0];
  if (reviewsArray && reviewsArray.length > 0) {
    reviewsArray.forEach((individualReview: Review) => {
      if (individualReview.rating >= 1 && individualReview.rating <= 5) {
        ratingCountsArray[5 - individualReview.rating] += 1;
      }
    });
  }
  return ratingCountsArray;
}

const calculateRatingStats = (agentReviewData: AgentReviewData) => {
  if (!agentReviewData) {
    return {
      ratingDistribution: [],
      ratingPercents: [],
    };
  }

  const ratingDistribution = getRatingDistribution(agentReviewData.rating_distribution, agentReviewData.reviews);
  const totalReviews = ratingDistribution.reduce((a: number, b: number) => a + b, 0);
  const ratingPercents = ratingDistribution.map((count: number) => ((count / (totalReviews || 1)) * 100).toFixed(1));

  return {
    ratingDistribution,
    ratingPercents,
  };
};

function AgentReviewAnalysis({
  agentId,
  agentName,
  agentOwner,
  onReviewChange,
  agentDetails,
  onClose,
}: AgentReviewAnalysisProps) {
  const { accounts } = useMsal();
  const currentUser = accounts[0] || {};
  const {
    data: agentReviewData,
    isLoading: isAgentReviewLoading,
    error: agentReviewError,
    refetch: refetchAgentReviews,
  } = useGetAgentReviewsQuery(agentId);
  const [deleteAgentReview, { isLoading: isDeleteReviewLoading }] = useDeleteAgentReviewMutation();
  const [postReview, { isLoading: isPostingReview }] = usePostAgentReviewMutation();
  const [putReview, { isLoading: isPuttingReview }] = usePutAgentReviewMutation();
  const [deletingReviewId, setDeletingReviewId] = useState<string | null>(null);
  const [showInlineReviewForm, setShowInlineReviewForm] = useState(false);
  const [reviewRating, setReviewRating] = useState<number | null>(null);
  const [reviewText, setReviewText] = useState('');
  const [isEditMode, setIsEditMode] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showSuccessSnackbar, setShowSuccessSnackbar] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [reviewToDelete, setReviewToDelete] = useState<string | null>(null);

  const { ratingDistribution, ratingPercents } = calculateRatingStats(agentReviewData);

  const handleDeleteReview = async (reviewId: string) => {
    setDeletingReviewId(reviewId);
    try {
      const res = await deleteAgentReview(reviewId).unwrap();
      if (res.status === 'success') {
        setShowInlineReviewForm(false);
        setIsEditMode(false);
        setReviewRating(null);
        setReviewText('');
        refetchAgentReviews();
        if (onReviewChange) onReviewChange();
        setSuccessMessage('Review deleted successfully!');
        setShowSuccessSnackbar(true);
      }
    } catch (e) {
      setSuccessMessage('Failed to delete review. Please try again.');
    } finally {
      setDeletingReviewId(null);
      setShowDeleteConfirmation(false);
      setReviewToDelete(null);
    }
  };

  const handleDeleteClick = (reviewId: string) => {
    setReviewToDelete(reviewId);
    setShowDeleteConfirmation(true);
  };

  const handleConfirmDelete = () => {
    if (reviewToDelete) {
      handleDeleteReview(reviewToDelete);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirmation(false);
    setReviewToDelete(null);
  };

  const existingUserReview =
    agentReviewData && agentReviewData.reviews && currentUser
      ? (() => {
          const userData = extractUserData(currentUser as UserAccount);
          return getUserReview(agentReviewData.reviews, userData.oid, userData.email, userData.name);
        })()
      : undefined;

  const handleWriteReviewClick = () => {
    setShowInlineReviewForm(true);
    setIsEditMode(false);
  };

  const handleEditReviewClick = () => {
    if (existingUserReview) {
      setReviewRating(existingUserReview.rating);
      setReviewText(existingUserReview.comment);
      setShowInlineReviewForm(true);
      setIsEditMode(true);
    }
  };

  const handleCancelInlineReview = () => {
    setShowInlineReviewForm(false);
    setIsEditMode(false);
    setReviewRating(null);
    setReviewText('');
  };

  const handleSubmitInlineReview = async () => {
    if (!reviewRating || !reviewText.trim()) {
      return;
    }

    try {
      if (isEditMode && existingUserReview) {
        await putReview({
          reviewId: existingUserReview.review_id,
          payload: {
            rating: reviewRating,
            comment: reviewText.trim(),
          },
        });
        setSuccessMessage('Rating updated successfully!');
      } else {
        const user = accounts[0] || {};
        const payload = createReviewPayload(reviewRating, reviewText, user.name || '', user.username || '');

        await postReview({
          agentId: agentId,
          payload,
        });
        setSuccessMessage('Rating submitted successfully!');
      }
      setShowSuccessSnackbar(true);
      handleCancelInlineReview();
      refetchAgentReviews();
      if (onReviewChange) onReviewChange();
      setTimeout(() => {
        if (onClose) onClose();
      }, 1500);
    } catch (error) {
      console.error('Failed to submit review:', error);
    }
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 1 }}>
      {isAgentReviewLoading ? (
        <Typography>Loading reviews...</Typography>
      ) : agentReviewError ? (
        <Typography color="error">Failed to load reviews.</Typography>
      ) : (
        <>
          <Typography variant="subtitle1" fontWeight={300} mb={0.5}>
            Detailed Reviews & Rating Analysis
          </Typography>

          <Typography
            variant="h6"
            fontWeight={600}
            mb={0.5}
            sx={{ display: 'flex', alignItems: 'center' }}
            fontSize="1.1rem"
          >
            {agentName}
            <MuiIcons.Star sx={{ color: '#FFD700', fontSize: 20, ml: 1, mr: 0.5 }} />
            <Typography variant="h6" fontWeight={500}>
              {agentReviewData.average_rating}
            </Typography>
            <Typography variant="subtitle2" color="text.secondary" sx={{ ml: 0.5 }}>
              Based on {agentReviewData.total_count} reviews
            </Typography>
          </Typography>

          <Typography variant="subtitle1" fontWeight={300} mb={1.5}>
            by {agentOwner}
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, mb: 1.5, width: '100%', alignItems: 'flex-start' }}>
            {/* Left Column - Rating Distribution and Rate this Agent */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="h6" fontWeight={600} mb="10px" fontSize="1.1rem">
                Rating Distribution
              </Typography>
              <Paper sx={{ p: 2, minHeight: 'auto', mb: 1 }}>
                {ratingDistribution.length > 0 &&
                  ratingDistribution.map((count: number, index: number) => {
                    const star = 5 - index;
                    return (
                      <Box key={star} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <MuiIcons.Star sx={{ color: '#FFD700', fontSize: 18, mr: 1 }} />
                        <Typography sx={{ minWidth: 24, mr: 2, fontWeight: 600 }}>{star}</Typography>
                        <LinearProgress
                          variant="determinate"
                          value={parseFloat(ratingPercents[index])}
                          sx={{
                            flex: 1,
                            height: 10,
                            borderRadius: 5,
                            mr: 2,
                            backgroundColor: '#e0e0e0',
                            '& .MuiLinearProgress-bar': {
                              backgroundColor: '#1976d2',
                            },
                          }}
                        />
                        <Typography sx={{ minWidth: 80, fontWeight: 500 }}>
                          {count} ({ratingPercents[index]}%)
                        </Typography>
                      </Box>
                    );
                  })}
              </Paper>

              <Paper sx={{ py: 1, textAlign: 'center', width: '100%' }}>
                {!showInlineReviewForm ? (
                  <>
                    <Typography variant="h6" fontWeight={600} mb={1}>
                      Rate this Agent
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mb={2}>
                      Share your experience with other users
                    </Typography>
                    {!existingUserReview ? (
                      <Button
                        variant="contained"
                        onClick={handleWriteReviewClick}
                        sx={{
                          py: 1,
                          px: 3,
                          borderRadius: 2,
                          fontWeight: 600,
                          textTransform: 'none',
                        }}
                        startIcon={<MuiIcons.Star />}
                      >
                        Write a review
                      </Button>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        You have already reviewed this agent. Use the edit icon next to your review to modify it.
                      </Typography>
                    )}
                  </>
                ) : (
                  <Box sx={{ textAlign: 'left', px: 1.5, py: 1 }}>
                    <Typography variant="subtitle1" fontWeight={500} mb={1} fontSize={14}>
                      Overall Rating *
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                      <Rating
                        value={reviewRating}
                        onChange={(event, newValue) => setReviewRating(newValue)}
                        sx={{ color: '#FFD700', mr: 2, fontSize: 20 }}
                      />
                      <Typography sx={{ color: '#888', fontWeight: 500, fontSize: 14 }}>Select rating</Typography>
                    </Box>
                    <Typography variant="subtitle2" fontWeight={600} mb={1} fontSize={14}>
                      Your Review *
                    </Typography>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      placeholder="Share your experience with this agent. What worked well? What could be improved?"
                      value={reviewText}
                      onChange={(e) => setReviewText(e.target.value)}
                      variant="outlined"
                      inputProps={{ maxLength: 1000 }}
                      sx={{ mb: 0.5 }}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <Typography variant="caption" color="text.secondary">
                        {reviewText.length}/1000 characters
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-start' }}>
                      <Button
                        variant="outlined"
                        onClick={handleCancelInlineReview}
                        sx={{ textTransform: 'none', px: 2, py: 0.5 }}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="contained"
                        onClick={handleSubmitInlineReview}
                        disabled={!reviewRating || !reviewText.trim() || isPostingReview || isPuttingReview}
                        sx={{ textTransform: 'none', px: 2, py: 0.5 }}
                      >
                        {isPostingReview || isPuttingReview
                          ? isEditMode
                            ? 'Updating...'
                            : 'Submitting...'
                          : isEditMode
                            ? 'Update'
                            : 'Submit'}
                      </Button>
                    </Box>
                  </Box>
                )}
              </Paper>
            </Box>

            {/* Right Column - Reviews */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="h6" fontWeight={600} mb={1} fontSize="1.1rem">
                Reviews
              </Typography>
              <Paper sx={{ p: 2, minHeight: 'auto', height: '100%', maxHeight: 380, overflowY: 'auto' }}>
                {agentReviewData && agentReviewData.reviews && agentReviewData.reviews.length > 0 ? (
                  agentReviewData.reviews.map((review: Review, index: number) => {
                    const userData = extractUserData(currentUser as UserAccount);
                    const isCurrentUserReview =
                      review.user_id === userData.oid ||
                      review.email === userData.email ||
                      review.username === userData.name;

                    return (
                      <Box
                        key={index}
                        sx={{
                          mb: 2,
                          pb: 1.5,
                          borderBottom: index < agentReviewData.reviews.length - 1 ? '1px solid #e0e0e0' : 'none',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                          <Box
                            sx={{
                              width: 32,
                              height: 32,
                              bgcolor: '#e3f2fd',
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              mr: 1.5,
                            }}
                          >
                            <Typography fontWeight={700} color="primary" fontSize={12}>
                              {review.username
                                ? review.username
                                    .split(' ')
                                    .map((n: string) => n[0])
                                    .join('')
                                    .toUpperCase()
                                : '?'}
                            </Typography>
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <Typography fontWeight={700} sx={{ mr: 2, fontSize: 14 }}>
                              {review.username || 'Anonymous'}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="caption" color="text.secondary">
                                {review.updated_at
                                  ? new Date(Number(review.updated_at) * 1000).toLocaleDateString()
                                  : review.created_at
                                    ? new Date(Number(review.created_at) * 1000).toLocaleDateString()
                                    : ''}
                              </Typography>
                            </Box>
                          </Box>
                          {isCurrentUserReview && (
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <IconButton size="small" onClick={handleEditReviewClick} sx={{ color: '#1976d2' }}>
                                <MuiIcons.Edit fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteClick(review.review_id)}
                                disabled={deletingReviewId === review.review_id || isDeleteReviewLoading}
                                sx={{ color: '#d32f2f' }}
                              >
                                <MuiIcons.Delete fontSize="small" />
                              </IconButton>
                            </Box>
                          )}
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5, ml: 4.5 }}>
                          <Typography fontWeight={700} sx={{ mr: 1 }}>
                            {review.rating}
                          </Typography>
                          <MuiIcons.Star sx={{ color: '#FFD700', fontSize: 18 }} />
                        </Box>

                        <Typography fontWeight={400} sx={{ ml: 4.5, color: 'text.secondary', fontSize: 14 }}>
                          {review.comment}
                        </Typography>
                      </Box>
                    );
                  })
                ) : (
                  <Typography color="text.secondary" textAlign="center" py={3}>
                    No reviews yet. Be the first to review this agent!
                  </Typography>
                )}
              </Paper>
            </Box>
          </Box>

          <ConfirmationDialog
            open={showDeleteConfirmation}
            title="Delete Review"
            message="Are you sure you want to delete this review?"
            confirmButtonText="Delete"
            cancelButtonText="Cancel"
            confirmButtonColor="error"
            isLoading={isDeleteReviewLoading}
            loadingText="Deleting..."
            onConfirm={handleConfirmDelete}
            onCancel={handleCancelDelete}
          />

          <Snackbar
            open={showSuccessSnackbar}
            autoHideDuration={3000}
            onClose={() => setShowSuccessSnackbar(false)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          >
            <Alert
              onClose={() => setShowSuccessSnackbar(false)}
              severity="success"
              sx={{
                width: '100%',
                backgroundColor: '#4caf50',
                color: 'white',
                '& .MuiAlert-icon': {
                  color: 'white',
                },
                '& .MuiIconButton-root': {
                  color: 'white',
                },
              }}
            >
              {successMessage}
            </Alert>
          </Snackbar>
        </>
      )}
    </Box>
  );
}

export default AgentReviewAnalysis;
